import { type FC, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
// Import UI Components
import { Button } from '@geneui/components';
// Import Components
import FilterFields from 'components/common/FilterFields';
// Import Hooks
import useFilterFieldsState from 'hooks/UseFilterFieldsState';
import useApplyOnEnter from 'hooks/UseApplyOnEnter';
// Import Services
import { FilterExistingValueChecker, FiltersFieldsMap } from 'components/common/FilterFields/service';
// Import Constants
import { l } from 'constants/common';
// Import Styles
import styles from './dataTableHeaderFilter.module.scss';

interface Props {
    defaultInputsData: AnyObject[];
    isApplyOnEnterEnabled?: boolean;
    onApply?: (data: AnyObject) => void;
    onReset?: () => void;
}

const DataTableHeaderFilter: FC<Props> = ({ defaultInputsData, isApplyOnEnterEnabled = true, onApply, onReset }) => {
    const { t } = useTranslation();

    const {
        filters,
        changeFilters,
        applyFilters: applyChangedFilters,
        resetFilters,
        hasUnappliedChanges,
        isResetDisabled,
        isApplyDisabled,
    } = useFilterFieldsState(defaultInputsData);

    const applyFilters = (data = filters, withOnApply) => {
        const resultData = FiltersFieldsMap(data);
        withOnApply && onApply?.(resultData);
    };

    const handleReset = () => {
        resetFilters();
        onReset?.();
    };

    const handleChange = (e, changeInputValue, customChange, index, filterInputData) => {
        const resultData = changeInputValue(e, customChange, cloneDeep(filters), index, filterInputData);
        changeFilters(resultData);
    };

    const handleApply = () => {
        if (FilterExistingValueChecker(filters)) {
            applyChangedFilters();
            applyFilters(filters, true);
        }
    };

    const { containerRef, handleInteraction } = useApplyOnEnter<HTMLDivElement>({
        onApply: handleApply,
        canApply: hasUnappliedChanges,
        isEnabled: isApplyOnEnterEnabled,
    });

    const init = () => {
        applyFilters(filters, false);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <div
            ref={containerRef}
            className={styles.container}
            onFocus={handleInteraction}
            onMouseDown={handleInteraction}
        >
            <div className={styles.filterFields}>
                <FilterFields filterInputsData={filters} handleChange={handleChange} />
            </div>
            <div className={styles.buttons}>
                <Button appearance="minimal" onClick={handleReset} disabled={isResetDisabled}>
                    {t(l.Reset)}
                </Button>
                <Button color="confirm" onClick={handleApply} disabled={isApplyDisabled}>
                    {t(l.Apply)}
                </Button>
            </div>
        </div>
    );
};

export default memo(DataTableHeaderFilter);
